<script setup lang="ts">
import { useProject } from '@/services/projects';
import { useUser } from '@/services/user';
import { useBreakpoints } from '@/utils/breakpoints';
import { usePush } from '@/utils/usePush';
import {
  faArrowUpRight,
  faBell,
  faCheck,
  faCheckCircle,
  faEllipsis,
  faEnvelope,
  faTriangleExclamation,
} from '@fortawesome/pro-light-svg-icons';
import type { Tab } from '@teemill/components';
import {
  FontAwesomeIcon,
  TmlButton,
  TmlDropdownActions,
  TmlDropdownContainer,
  TmlSpinner,
  TmlTabs,
} from '@teemill/components';
import { useChatVersion } from '@teemill/modules/chat';
import { useModal } from '@teemill/modules/modals';
import { useNotifications } from '@teemill/modules/notifications';
import type { ComputedRef } from 'vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import TeemillHeaderNotificationList from './TeemillHeaderNotificationList.vue';
import TeemillHeaderNotificationsProjectList from './TeemillHeaderNotificationsProjectList.vue';
import TeemillNotificationIcon from './TeemillNotificationIcon.vue';

const breakpoints = useBreakpoints();
const project = useProject();
const route = useRoute();
const router = useRouter();
const user = useUser();

const greaterOrEqualToSm = breakpoints.greaterOrEqual('sm');
const containerOpen = ref(false);
const settingsContainerOpen = ref(false);
const activeTab = ref();

const tabs = ref<{ tab: Tab; hide: ComputedRef<boolean> | boolean }[]>([
  {
    tab: {
      text: user.active.value?.username
        ? `@${user.active.value.username}`
        : (user.active.value?.firstName ?? 'Me'),
      name: 'user',
    },
    hide: false,
  },
  {
    tab: {
      text: project.value?.name as string,
      name: `project:${project.value?.id}`,
    },
    hide: computed(() => !project.value),
  },
  {
    tab: { text: 'Projects', name: 'projects' },
    hide: false,
  },
]);

const visibleTabs = computed(() => tabs.value.filter((tab) => !tab.hide).map((tab) => tab.tab));

const search = computed(() => (activeTab.value ? `group:${activeTab.value}` : undefined));

watch(
  containerOpen,
  (isOpen) => {
    if (!isOpen) {
      settingsContainerOpen.value = false;
    } else {
      activeTab.value = 'user';
    }
  },
  { immediate: true },
);

const readAllNotifications = () => {
  user.notifications.readAll();
  settingsContainerOpen.value = false;
  containerOpen.value = false;
};

// Open the notifications dropdown if the query parameter is present
onMounted(() => {
  if (route.query.notification === 'true') {
    containerOpen.value = true;
    router.replace({ query: { ...route.query, notification: undefined } });
  }
});

const push = usePush();
const chatVersion = useChatVersion();

const onSubscribeToPush = async () => {
  const result = await useModal('confirm').open({
    title: 'Notifications',
    description: "Turn on device notifications so you don't miss out on important activity",
    actions: { confirm: 'Enable', cancel: 'Snooze' },
  });

  if (result === 'cancel') {
    push.dismiss();

    return;
  }

  const subscribed = await push.subscribe();

  switch (subscribed) {
    case 'aborted':
      useNotifications().nudge('Notifications are disabled or blocked by user', {
        level: 'default',
      });
      break;
    case 'failed':
      useNotifications().nudge('Failed to subscribe to notifications', {
        level: 'danger',
      });
      break;
    case 'subscribed':
      useNotifications().nudge('Subscribed to real-time notifications', {
        level: 'success',
      });
      break;
  }
};
</script>

<template>
  <TmlDropdownContainer
    v-model:open="containerOpen"
    align="left"
    width="420px"
    offset="1rem"
    max-height="80vh"
  >
    <template #trigger>
      <TmlButton class="!rounded-full" @click="containerOpen = !containerOpen">
        <TeemillNotificationIcon />
      </TmlButton>
    </template>
    <div class="flex items-center justify-between mx-4 mr-12 sm:mr-4">
      <h3 class="my-3">Notifications</h3>

      <div class="flex items-center gap-2">
        <template v-if="chatVersion === 'alpha'">
          <template v-if="push.isSupported.value">
            <FontAwesomeIcon
              v-if="push.isSubscribed.value"
              v-tooltip="'You are subscribed to real-time notifications'"
              :icon="faCheckCircle"
            />
            <TmlButton v-else class="touch-target size-6" @click="onSubscribeToPush()">
              <FontAwesomeIcon
                v-if="!push.loading.value"
                v-tooltip="'Subscribe to real-time notifications'"
                :icon="faBell"
              />
              <TmlSpinner v-else />
            </TmlButton>
          </template>
          <template v-else>
            <TmlButton
              v-tooltip="'Real-time notifications are not supported in this browser'"
              class="touch-target size-6"
            >
              <FontAwesomeIcon :icon="faTriangleExclamation" />
            </TmlButton>
          </template>
        </template>
        <TmlDropdownContainer
          v-if="!greaterOrEqualToSm"
          v-model:open="settingsContainerOpen"
          align="left"
          vertical-align="below"
          max-height="240px"
          as="dropdown"
        >
          <template #trigger>
            <TmlButton
              class="touch-target size-6"
              @click="settingsContainerOpen = !settingsContainerOpen"
            >
              <FontAwesomeIcon :icon="faEllipsis" />
            </TmlButton>
          </template>
          <TmlDropdownActions
            :actions="[
              {
                label: 'Mark all as read',
                icon: faCheck,
                handler: readAllNotifications,
              },
            ]"
          />
        </TmlDropdownContainer>
        <RouterLink
          v-else
          class="flex items-center gap-1.5 text-link cursor-pointer"
          :to="{
            name: 'user.inbox',
            query: {
              'search-notification': 'group:user',
            },
          }"
          @click="containerOpen = false"
        >
          <FontAwesomeIcon class="size-4 mt-0.5" :icon="faEnvelope" />
          <span>Inbox</span>
        </RouterLink>
      </div>
    </div>
    <TmlTabs v-model:active="activeTab" :tabs="visibleTabs" fill equal-width>
      <template #tab="{ tab }">
        <div class="font-bold py-2 text-center">
          <p class="px-1 overflow-hidden text-ellipsis">{{ tab.text }}</p>
        </div>
      </template>
    </TmlTabs>

    <Transition name="fade">
      <TmlButton
        v-if="push.isPromptable.value"
        class="w-full flex items-center gap-2 justify-between bg-[--tml-primary-color] text-white py-2 px-4"
        @click="onSubscribeToPush"
      >
        <div class="flex items-center gap-3">
          <FontAwesomeIcon v-if="!push.loading.value" class="size-4" :icon="faBell" />
          <TmlSpinner v-else class="size-4" />
          <span> Setup device notifications </span>
        </div>

        <FontAwesomeIcon class="size-4" :icon="faArrowUpRight" />
      </TmlButton>
    </Transition>

    <TeemillHeaderNotificationsProjectList v-if="activeTab === 'projects'" />
    <TeemillHeaderNotificationList v-else :search="search" @close="containerOpen = false" />
  </TmlDropdownContainer>
</template>
